import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { ProgressRadar } from '../components/Radar';
import { ExchangeTopics } from '../components/ExchangeTopics';
import { Progress } from '../components/ui/progress';
import { ShieldCheck, BookOpen, MessagesSquare } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function ParentHome() {
  const { user } = useAuth();
  const [children, setChildren] = useState(null);
  const [radars, setRadars] = useState({});
  const { t, dateLocale } = useI18n();

  useEffect(() => {
    api.get('/parent/children').then(async (r) => {
      setChildren(r.data.children);
      const entries = await Promise.all(
        r.data.children.map((c) =>
          api.get(`/parent/children/${c.user_id}/radar`).then((rr) => [c.user_id, rr.data.radar]).catch(() => [c.user_id, []])
        )
      );
      setRadars(Object.fromEntries(entries));
    }).catch(() => setChildren([]));
  }, []);

  const translateRadar = (items) => (items || []).map((r) => ({ ...r, axis: t(`axes.${r.key}`, undefined, r.axis) }));

  return (
    <div data-testid="parent-dashboard">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('parent.hello', { name: user?.name })}</h1>
        <p className="text-base sm:text-lg text-brume mt-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-or-400" strokeWidth={2.5} />
          {t('parent.intro')}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        {(children || []).map((c, i) => (
          <motion.div key={c.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: i * 0.1 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8"
            data-testid={`parent-child-card-${c.user_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-creme">{c.name}</h2>
                {c.age && <p className="text-sm text-brume font-semibold">{t('parent.years', { age: c.age })}</p>}
                {c.consent_at && (
                  <p className="text-xs text-brume mt-1" data-testid={`parent-consent-${c.user_id}`}>
                    {t('parent.consentAt', { date: new Date(c.consent_at).toLocaleDateString(dateLocale) })}
                  </p>
                )}
              </div>
              <span className="text-sm font-bold bg-or-400/15 text-or-300 px-4 py-2 rounded-full flex items-center gap-2">
                <BookOpen className="w-4 h-4" strokeWidth={2.5} />
                {t('parent.chaptersCount', { done: c.chapters_completed, total: c.chapters_total })}
              </span>
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-sm font-bold text-brume mb-2">
                <span>{t('parent.progression')}</span><span data-testid={`parent-child-percent-${c.user_id}`} className="text-creme">{c.percent}%</span>
              </div>
              <Progress value={c.percent} className="h-3 rounded-full bg-nuit-700" />
            </div>
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-creme mb-2">{t('parent.radar')}</h3>
              <ProgressRadar data={translateRadar(radars[c.user_id])} testid={`parent-radar-${c.user_id}`} />
            </div>
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-creme mb-3 flex items-center gap-2">
                <MessagesSquare className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('exchange.sectionTitle')}
              </h3>
              <ExchangeTopics youthId={c.user_id} testid={`parent-exchange-${c.user_id}`} />
            </div>
          </motion.div>
        ))}
        {children && children.length === 0 && (
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-10 text-center text-brume md:col-span-2" data-testid="parent-no-children">
            <p className="font-bold text-lg text-creme">{t('parent.noChildrenTitle')}</p>
            <p className="text-sm mt-2">{t('parent.noChildrenText')}</p>
          </div>
        )}
        {!children && <p className="text-brume" data-testid="parent-loading">{t('common.loading')}</p>}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.2 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 mt-10" data-testid="parent-exchange-general">
        <h2 className="text-2xl font-semibold text-creme mb-2 flex items-center gap-2">
          <MessagesSquare className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('exchange.general')}
        </h2>
        <p className="text-sm text-brume mb-5">{t('exchange.generalIntro')}</p>
        <ExchangeTopics testid="parent-exchange-general-list" />
      </motion.div>
    </div>
  );
}
